import { createApi } from '@reduxjs/toolkit/query/react';
import { merge } from 'lodash-es';
import { baseQueryWithAuth } from '@/app/baseQuery';
import defaultProfileSettings from '@/configs/defaultProfileSettings';
import type { Profile } from './types/profile-types';

interface GetProfileParams {
  profileId: string;
  include?: string;
}

interface GetProfileByBlogSlugParams {
  slug: string;
  withTags?: boolean;
}

export const profilesApi = createApi({
  reducerPath: 'profilesApi',
  baseQuery: baseQueryWithAuth,
  tagTypes: ['Profile'],
  endpoints: builder => ({
    /**
     * GET /profiles/{profile-id} - Get a single profile by ID
     */
    getProfile: builder.query<Profile, GetProfileParams>({
      query: ({ profileId, include = 'my-communities' }) => ({
        url: `profiles/${profileId}`,
        params: { include }
      }),
      transformResponse: (response: any) => {
        // Merge with default settings
        const profile = response as Profile;
        if (profile.settings) {
          profile.settings = merge({}, defaultProfileSettings, profile.settings);
        }
        return profile;
      },
      providesTags: (_result, _error, { profileId }) => [{ type: 'Profile', id: profileId }]
    }),

    /**
     * GET /profiles - Get profile by blog slug
     */
    getProfileByBlogSlug: builder.query<Profile, GetProfileByBlogSlugParams>({
      query: ({ slug, withTags = false }) => ({
        url: 'profiles',
        params: {
          'filters[slug]': slug.toLowerCase(),
          ...(withTags ? { include: 'tags' } : {})
        }
      }),
      transformResponse: (response: any) => {
        // The response is an array, get the first item
        const profiles = Array.isArray(response) ? response : [response];
        if (profiles.length > 0) {
          const profile = profiles[0] as Profile;
          if (profile.settings) {
            profile.settings = merge({}, defaultProfileSettings, profile.settings);
          }
          return profile;
        }
        throw new Error('блог не найден');
      },
      providesTags: result => (result ? [{ type: 'Profile', id: result.id }] : [])
    })
  })
});

export const { useGetProfileQuery, useGetProfileByBlogSlugQuery } = profilesApi;
